/**
 * HALO Employee Card
 * Real-time biometric wellness data display
 * Design: Clean card with emotion badges and metric bars
 */

import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { formatDistanceToNow } from "date-fns";
import {
  User,
  AlertTriangle,
  Phone,
  Activity,
  Smile,
  Frown,
  Meh,
  Coffee,
  Zap,
  Monitor,
  MousePointer,
  Hand,
  Moon,
  Wifi,
  WifiOff,
  Shield,
  EyeOff,
} from "lucide-react";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type EmotionState =
  | "Focused"
  | "Happy"
  | "Engaged"
  | "Neutral"
  | "Confused"
  | "Tired"
  | "Stressed"
  | "Frustrated";

export type HandActivity =
  | "Typing"
  | "Mouse"
  | "Gesturing"
  | "Idle"
  | "Phone Detected";

export interface AttentionMetrics {
  eye_contact_percent: number;
  gaze_direction: "center" | "left" | "right" | "up" | "down";
  gaze_stability_score: number;
  screen_focus_duration_sec: number;
}

export interface FatigueDetails {
  blink_rate_per_minute: number;
  average_blink_duration_ms: number;
  prolonged_closures_count: number;
  yawn_detected: boolean;
}

export interface EmployeeWellnessData {
  id: string;
  name: string;
  email: string;
  avatar_url?: string;
  department?: string;

  // Wellness Metrics
  emotion_state: EmotionState | null;
  emotion_confidence: number | null;
  posture_score: number | null;
  hand_activity: HandActivity;
  fatigue_index: number | null;
  fatigue_details: FatigueDetails | null;
  attention_metrics: AttentionMetrics;

  // Privacy Settings - indicates what data the user has hidden
  privacy_hidden?: {
    emotion?: boolean;
    posture?: boolean;
    fatigue?: boolean;
    activity?: boolean;
  };

  // Status
  is_online: boolean;
  last_updated: Date;
}

export interface EmployeeCardProps {
  employee: EmployeeWellnessData;
  isCompact?: boolean;
  onViewDetails?: (employeeId: string) => void;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get emotion display config based on HALO design guide
 */
const getEmotionDisplay = (emotion: EmotionState | null) => {
  if (!emotion)
    return {
      icon: <Meh className="w-3.5 h-3.5" />,
      color: "muted",
      label: "No Data",
    };

  const emotions: Record<
    EmotionState,
    {
      icon: React.ReactNode;
      color: string;
      label: string;
    }
  > = {
    Focused: {
      icon: <Zap className="w-3.5 h-3.5" />,
      color: "signal-blue",
      label: "Focused",
    },
    Happy: {
      icon: <Smile className="w-3.5 h-3.5" />,
      color: "signal-green",
      label: "Happy",
    },
    Engaged: {
      icon: <Activity className="w-3.5 h-3.5" />,
      color: "primary",
      label: "Engaged",
    },
    Neutral: {
      icon: <Meh className="w-3.5 h-3.5" />,
      color: "muted-slate",
      label: "Neutral",
    },
    Confused: {
      icon: <AlertTriangle className="w-3.5 h-3.5" />,
      color: "muted-slate",
      label: "Confused",
    },
    Tired: {
      icon: <Moon className="w-3.5 h-3.5" />,
      color: "signal-amber",
      label: "Tired",
    },
    Stressed: {
      icon: <Frown className="w-3.5 h-3.5" />,
      color: "signal-amber",
      label: "Stressed",
    },
    Frustrated: {
      icon: <Frown className="w-3.5 h-3.5" />,
      color: "signal-amber",
      label: "Frustrated",
    },
  };

  return emotions[emotion];
};

/**
 * Get activity display config
 */
const getActivityDisplay = (activity: HandActivity) => {
  const activities: Record<
    HandActivity,
    {
      icon: React.ReactNode;
      label: string;
      color: string;
    }
  > = {
    Typing: {
      icon: <Monitor className="w-3.5 h-3.5" />,
      label: "Typing",
      color: "signal-blue",
    },
    Mouse: {
      icon: <MousePointer className="w-3.5 h-3.5" />,
      label: "Mouse",
      color: "signal-blue",
    },
    Gesturing: {
      icon: <Hand className="w-3.5 h-3.5" />,
      label: "Gesturing",
      color: "muted-slate",
    },
    Idle: {
      icon: <Coffee className="w-3.5 h-3.5" />,
      label: "Idle",
      color: "muted-slate",
    },
    "Phone Detected": {
      icon: <Phone className="w-3.5 h-3.5" />,
      label: "Phone",
      color: "signal-amber",
    },
  };

  return activities[activity];
};

/**
 * Get posture color based on score
 */
const getPostureColor = (score: number | null): string => {
  if (score === null) return "muted-slate";
  if (score >= 75) return "signal-green";
  if (score >= 50) return "signal-amber";
  return "signal-crimson";
};

/**
 * Get fatigue color based on index
 */
const getFatigueColor = (index: number | null): string => {
  if (index === null) return "muted-slate";
  if (index <= 0.5) return "signal-green";
  if (index <= 0.7) return "signal-amber";
  return "signal-crimson";
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface BadgeProps {
  children: React.ReactNode;
  variant?: "green" | "blue" | "amber" | "crimson" | "muted" | "shield";
  icon?: React.ReactNode;
}

const Badge: React.FC<BadgeProps> = ({ children, variant = "muted", icon }) => {
  const variants = {
    green: "halo-badge halo-badge--green",
    blue: "halo-badge halo-badge--blue",
    amber: "halo-badge halo-badge--amber",
    crimson: "halo-badge halo-badge--crimson",
    muted: "halo-badge halo-badge--muted",
    shield: "halo-badge halo-badge--shield",
  };

  return (
    <span className={variants[variant]}>
      {icon}
      {children}
    </span>
  );
};

interface ProgressBarProps {
  value: number;
  color: string;
  size?: "sm" | "md";
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  color,
  size = "md",
}) => {
  const colorClasses: Record<string, string> = {
    "signal-green": "bg-signal-green",
    "signal-blue": "bg-signal-blue",
    "signal-amber": "bg-signal-amber",
    "signal-crimson": "bg-signal-crimson",
  };

  return (
    <div className={`halo-progress ${size === "sm" ? "h-1.5" : ""}`}>
      <motion.div
        className={`halo-progress__bar ${
          colorClasses[color] || "bg-muted-slate"
        }`}
        initial={{ width: 0 }}
        animate={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      />
    </div>
  );
};

// ============================================================================
// COMPACT CARD (Grid View)
// ============================================================================

const CompactCard: React.FC<{
  employee: EmployeeWellnessData;
  onViewDetails?: (id: string) => void;
}> = ({ employee, onViewDetails }) => {
  const emotionDisplay = useMemo(
    () => getEmotionDisplay(employee.emotion_state),
    [employee.emotion_state],
  );
  const activityDisplay = useMemo(
    () => getActivityDisplay(employee.hand_activity),
    [employee.hand_activity],
  );

  const hasAlert =
    (employee.fatigue_index !== null && employee.fatigue_index > 0.7) ||
    (employee.posture_score !== null && employee.posture_score < 50) ||
    employee.hand_activity === "Phone Detected";
  const postureColor = getPostureColor(employee.posture_score);
  const fatigueColor = getFatigueColor(employee.fatigue_index);

  // Composite wellness score
  const emotionScoreMap: Record<string, number> = {
    Focused: 90,
    Happy: 95,
    Engaged: 85,
    Neutral: 70,
    Confused: 50,
    Tired: 40,
    Stressed: 35,
    Frustrated: 30,
  };

  const wellnessScore =
    employee.posture_score !== null &&
    employee.fatigue_index !== null &&
    employee.emotion_state
      ? Math.round(
          employee.posture_score * 0.4 +
            (1 - employee.fatigue_index) * 100 * 0.3 +
            (emotionScoreMap[employee.emotion_state] || 50) * 0.3,
        )
      : null;

  return (
    <motion.div
      className={`halo-card cursor-pointer group ${
        hasAlert ? "halo-card--alert" : ""
      }`}
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onViewDetails?.(employee.id)}
      layout
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className="relative">
            {employee.avatar_url ? (
              <img
                src={employee.avatar_url}
                alt={employee.name}
                className="w-10 h-10 rounded-xl object-cover ring-2 ring-transparent group-hover:ring-primary/30 transition-all duration-standard"
              />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-surface-elevated flex items-center justify-center">
                <User className="w-5 h-5 text-muted-slate" />
              </div>
            )}
            {/* Online indicator */}
            <div
              className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-surface-dark ${
                employee.is_online ? "bg-signal-green" : "bg-muted-slate"
              }`}
            />
          </div>

          <div className="min-w-0">
            <h3 className="font-medium text-foreground truncate">
              {employee.name}
            </h3>
            <p className="text-xs text-muted-slate truncate">
              {employee.department}
            </p>
          </div>
        </div>

        {/* Status */}
        <div className="flex items-center gap-2">
          {hasAlert && (
            <div className="w-2 h-2 rounded-full bg-signal-amber animate-pulse" />
          )}
          <span
            className={`text-xs font-mono font-semibold tabular-nums ${
              wellnessScore === null
                ? "text-muted-slate"
                : wellnessScore >= 75
                ? "text-signal-green"
                : wellnessScore >= 50
                ? "text-signal-amber"
                : "text-signal-crimson"
            }`}
          >
            {wellnessScore !== null ? `${wellnessScore}%` : "—"}
          </span>
        </div>
      </div>

      {/* Badges */}
      <div className="flex flex-wrap gap-2 mb-4">
        {employee.privacy_hidden?.emotion ? (
          <Badge variant="shield" icon={<EyeOff className="w-3.5 h-3.5" />}>
            Emotion Hidden
          </Badge>
        ) : (
          <Badge variant={emotionDisplay.color as any} icon={emotionDisplay.icon}>
            {emotionDisplay.label}
          </Badge>
        )}
        {employee.privacy_hidden?.activity ? (
          <Badge variant="shield" icon={<EyeOff className="w-3.5 h-3.5" />}>
            Activity Hidden
          </Badge>
        ) : (
          <Badge
            variant={activityDisplay.color as any}
            icon={activityDisplay.icon}
          >
            {activityDisplay.label}
          </Badge>
        )}
      </div>

      {/* Metrics */}
      <div className="space-y-3">
        {/* Posture */}
        <div>
          <div className="flex justify-between text-xs mb-1.5">
            <span className="text-muted-slate flex items-center gap-1.5">
              Posture
              {employee.privacy_hidden?.posture && (
                <Shield className="w-3 h-3 text-muted-slate/60" />
              )}
            </span>
            <span className={`text-${postureColor} font-mono`}>
              {employee.privacy_hidden?.posture ? (
                <span className="text-muted-slate">Hidden</span>
              ) : employee.posture_score !== null ? (
                `${employee.posture_score}%`
              ) : (
                "—"
              )}
            </span>
          </div>
          {!employee.privacy_hidden?.posture && (
            <ProgressBar
              value={employee.posture_score || 0}
              color={postureColor}
              size="sm"
            />
          )}
          {employee.privacy_hidden?.posture && (
            <div className="h-1.5 bg-surface-elevated rounded-full opacity-50" />
          )}
        </div>

        {/* Fatigue */}
        <div>
          <div className="flex justify-between text-xs mb-1.5">
            <span className="text-muted-slate flex items-center gap-1.5">
              Fatigue
              {employee.privacy_hidden?.fatigue && (
                <Shield className="w-3 h-3 text-muted-slate/60" />
              )}
            </span>
            <span className={`text-${fatigueColor} font-mono`}>
              {employee.privacy_hidden?.fatigue ? (
                <span className="text-muted-slate">Hidden</span>
              ) : employee.fatigue_index !== null ? (
                `${Math.round(employee.fatigue_index * 100)}%`
              ) : (
                "—"
              )}
            </span>
          </div>
          {!employee.privacy_hidden?.fatigue && (
            <ProgressBar
              value={
                employee.fatigue_index !== null ? employee.fatigue_index * 100 : 0
              }
              color={fatigueColor}
              size="sm"
            />
          )}
          {employee.privacy_hidden?.fatigue && (
            <div className="h-1.5 bg-surface-elevated rounded-full opacity-50" />
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 pt-3 border-t border-glass-grey">
        <p className="text-xs text-muted-slate">
          updated{" "}
          {employee.last_updated.getTime() === new Date(0).getTime()
            ? "Never"
            : formatDistanceToNow(employee.last_updated, { addSuffix: true })}
        </p>
      </div>
    </motion.div>
  );
};

// ============================================================================
// FULL CARD (Detailed View)
// ============================================================================

const FullCard: React.FC<{ employee: EmployeeWellnessData }> = ({
  employee,
}) => {
  const emotionDisplay = useMemo(
    () => getEmotionDisplay(employee.emotion_state),
    [employee.emotion_state],
  );
  const activityDisplay = useMemo(
    () => getActivityDisplay(employee.hand_activity),
    [employee.hand_activity],
  );

  const postureColor = getPostureColor(employee.posture_score);
  const fatigueColor = getFatigueColor(employee.fatigue_index);

  return (
    <motion.div
      className="halo-card"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="relative">
            {employee.avatar_url ? (
              <img
                src={employee.avatar_url}
                alt={employee.name}
                className="w-14 h-14 rounded-xl object-cover"
              />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-surface-elevated flex items-center justify-center">
                <User className="w-7 h-7 text-muted-slate" />
              </div>
            )}
            <div
              className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-surface-dark ${
                employee.is_online ? "bg-signal-green" : "bg-muted-slate"
              }`}
            />
          </div>

          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {employee.name}
            </h3>
            <p className="text-sm text-muted-slate">{employee.department}</p>
            <p className="text-xs text-muted-slate mt-0.5">
              Updated{" "}
              {employee.last_updated.getTime() === new Date(0).getTime()
                ? "Never"
                : formatDistanceToNow(employee.last_updated, {
                    addSuffix: true,
                  })}
            </p>
          </div>
        </div>

        {/* Connection Status */}
        <div className="flex items-center gap-1.5 text-xs text-muted-slate">
          {employee.is_online ? (
            <>
              <Wifi className="w-3.5 h-3.5 text-signal-green" />
              <span>Online</span>
            </>
          ) : (
            <>
              <WifiOff className="w-3.5 h-3.5 text-muted-slate" />
              <span>Offline</span>
            </>
          )}
        </div>
      </div>

      {/* Status Badges */}
      <div className="flex flex-wrap gap-2 mb-6">
        {employee.privacy_hidden?.emotion ? (
          <Badge variant="shield" icon={<Shield className="w-3.5 h-3.5" />}>
            Emotion Data Hidden
          </Badge>
        ) : (
          <Badge variant={emotionDisplay.color as any} icon={emotionDisplay.icon}>
            {emotionDisplay.label}
          </Badge>
        )}
        {employee.privacy_hidden?.activity ? (
          <Badge variant="shield" icon={<Shield className="w-3.5 h-3.5" />}>
            Activity Data Hidden
          </Badge>
        ) : (
          <Badge
            variant={activityDisplay.color as any}
            icon={activityDisplay.icon}
          >
            {activityDisplay.label}
          </Badge>
        )}
        {!employee.privacy_hidden?.fatigue && employee.fatigue_details?.yawn_detected && (
          <Badge variant="amber" icon={<Moon className="w-3.5 h-3.5" />}>
            Yawning
          </Badge>
        )}
      </div>

      {/* Metrics Grid */}
      <div className="space-y-5">
        {/* Posture Score */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-medium text-muted-slate uppercase tracking-wider flex items-center gap-2">
              Posture Score
              {employee.privacy_hidden?.posture && (
                <Shield className="w-3.5 h-3.5 text-muted-slate/60" />
              )}
            </span>
            <span className={`text-sm font-mono text-${postureColor}`}>
              {employee.privacy_hidden?.posture ? (
                <span className="text-muted-slate">Hidden by User</span>
              ) : employee.posture_score !== null ? (
                `${employee.posture_score}/100`
              ) : (
                "—"
              )}
            </span>
          </div>
          {!employee.privacy_hidden?.posture ? (
            <ProgressBar
              value={employee.posture_score || 0}
              color={postureColor}
            />
          ) : (
            <div className="h-2 bg-surface-elevated rounded-full opacity-50 flex items-center justify-center">
              <p className="text-xs text-muted-slate">Privacy Enabled</p>
            </div>
          )}
        </div>

        {/* Fatigue Index */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-medium text-muted-slate uppercase tracking-wider flex items-center gap-2">
              Fatigue Index
              {employee.privacy_hidden?.fatigue && (
                <Shield className="w-3.5 h-3.5 text-muted-slate/60" />
              )}
            </span>
            <span className={`text-sm font-mono text-${fatigueColor}`}>
              {employee.privacy_hidden?.fatigue ? (
                <span className="text-muted-slate">Hidden by User</span>
              ) : employee.fatigue_index !== null ? (
                employee.fatigue_index.toFixed(2)
              ) : (
                "—"
              )}
            </span>
          </div>
          {!employee.privacy_hidden?.fatigue ? (
            <>
              <ProgressBar
                value={
                  employee.fatigue_index !== null ? employee.fatigue_index * 100 : 0
                }
                color={fatigueColor}
              />
              <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-slate">
                <span>
                  {employee.fatigue_details?.blink_rate_per_minute || 0} blinks/min
                </span>
                <span>•</span>
                <span>
                  {employee.fatigue_details?.average_blink_duration_ms || 0}ms avg
                </span>
              </div>
            </>
          ) : (
            <div className="h-2 bg-surface-elevated rounded-full opacity-50 flex items-center justify-center">
              <p className="text-xs text-muted-slate">Privacy Enabled</p>
            </div>
          )}
        </div>

        {/* Attention Metrics */}
        <div className="pt-4 border-t border-glass-grey">
          <h4 className="text-xs font-medium text-muted-slate uppercase tracking-wider mb-3">
            Attention
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="halo-metric">
              <span className="halo-metric__label">Eye Contact</span>
              <span className="halo-metric__value">
                {employee.attention_metrics?.eye_contact_percent || 0}%
              </span>
            </div>
            <div className="halo-metric">
              <span className="halo-metric__label">Gaze Stability</span>
              <span className="halo-metric__value">
                {employee.attention_metrics?.gaze_stability_score || 0}%
              </span>
            </div>
            <div className="halo-metric">
              <span className="halo-metric__label">Focus Duration</span>
              <span className="halo-metric__value">
                {Math.round(
                  (employee.attention_metrics?.screen_focus_duration_sec || 0) /
                    60,
                )}
                m
              </span>
            </div>
            <div className="halo-metric">
              <span className="halo-metric__label">Gaze Direction</span>
              <span className="halo-metric__value capitalize">
                {employee.attention_metrics?.gaze_direction || "—"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const EmployeeCard: React.FC<EmployeeCardProps> = ({
  employee,
  isCompact = false,
  onViewDetails,
}) => {
  if (isCompact) {
    return <CompactCard employee={employee} onViewDetails={onViewDetails} />;
  }

  return <FullCard employee={employee} />;
};

// ============================================================================
// TEAM PULSE GRID
// ============================================================================

export interface TeamPulseGridProps {
  employees: EmployeeWellnessData[];
  onViewDetails?: (employeeId: string) => void;
  isLoading?: boolean;
}

export const TeamPulseGrid: React.FC<TeamPulseGridProps> = ({
  employees,
  onViewDetails,
  isLoading = false,
}) => {
  // Sort employees: alerts first, then online, then offline
  const sortedEmployees = useMemo(() => {
    return [...employees].sort((a, b) => {
      const aHasAlert =
        (a.fatigue_index !== null && a.fatigue_index > 0.7) ||
        (a.posture_score !== null && a.posture_score < 50) ||
        a.hand_activity === "Phone Detected";
      const bHasAlert =
        (b.fatigue_index !== null && b.fatigue_index > 0.7) ||
        (b.posture_score !== null && b.posture_score < 50) ||
        b.hand_activity === "Phone Detected";
      if (aHasAlert && !bHasAlert) return -1;
      if (!aHasAlert && bHasAlert) return 1;
      if (a.is_online && !b.is_online) return -1;
      if (!a.is_online && b.is_online) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [employees]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-48 rounded-xl halo-shimmer" />
        ))}
      </div>
    );
  }

  if (employees.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-slate">
        <User className="w-12 h-12 mb-4 opacity-30" />
        <p className="text-lg font-medium text-foreground mb-1">
          No team members
        </p>
        <p className="text-sm">
          Employees will appear here when they start monitoring
        </p>
      </div>
    );
  }

  return (
    <motion.div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
      layout
    >
      <AnimatePresence mode="popLayout">
        {sortedEmployees.map((employee, index) => (
          <motion.div
            key={employee.id}
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2, delay: index * 0.05 }}
          >
            <EmployeeCard
              employee={employee}
              isCompact
              onViewDetails={onViewDetails}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </motion.div>
  );
};

export default EmployeeCard;
